import { buildDirectStudyResultsViewUrl, buildStudyBuilderViewUrl } from '../../resultsNavigation'
import { DirectStudyDeletion } from '../direct-studies/DirectStudyDeletion'
import { DirectStudyDeleteControl } from '../direct-studies/DirectStudyDeleteControl'
import { RequestFailureBanner } from '../RequestFailureBanner'
/**
 * What: Mounts the "Create new test" page on the real /study-builder route. This is the page: it reads the
 *   bootstrap through the page lifecycle (`pages/study-builder.page.ts`) and shows the loading skeleton, a
 *   named error, or the editor. ShopifySelfPagedView renders it with `key={visit}`, so a new draft, setup or
 *   shop is a fresh mount with a fresh lifecycle.
 * Transport / data-flow: Real data throughout; the builder's queries and form exist only while the editor is mounted.
 */
import type { useAppBridge } from '@shopify/app-bridge-react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { Banner } from './Banner'
import { StudyBuilderEditor } from './StudyBuilderEditor'
import { StudyBuilderLoadError } from './StudyBuilderLoadError'
import { ShopifyBackActionButton } from '../ShopifyPageActionButtons'
import { ShopifyPageSkeleton } from '../ShopifyPageSkeleton'
import type { BuilderLoadErrorDictionary } from '../../../src/shared/testfeed-connection-status-contract'
import { useStudyBuilderPage } from '../../hooks/study-builder/useStudyBuilderPage'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import { type ShopifyAppViewState } from '../../resultsNavigation'
import type { StudySetup } from '../../types'

/** Page-shell wording before evaluation exists; a recorded title always takes precedence. */
function pageHeading(name: string | undefined, entry?: 'new'): string {
  const title = name?.trim()
  if (title) return title
  const copy = shopifyComponentCopy.studyBuilderPage
  if (entry === 'new') return copy.newTestHeading
  return copy.untitledHeading
}

export function StudyBuilderRoute({
  appView,
  visit,
  shopify,
  expectedShop,
  verifiedScope,
  readiness,
  onSetupSaved,
  onStudyPrepared,
  onContactStudySupport,
  onBack,
}: {
  appView: Extract<ShopifyAppViewState, { view: 'study-builder' }>
  /** The visit key this route is mounted with; the lifecycle drops events from any other visit. */
  visit: string
  shopify: ReturnType<typeof useAppBridge>
  expectedShop: string | null
  verifiedScope: VerifiedTestFeedConnectionScope | null
  /** The connected status; `builderLoadError` is the Worker's load-error words, present before the builder mounts. */
  readiness: { code: string; builderLoadError?: BuilderLoadErrorDictionary | null } | null
  onStudyPrepared?: (studyId: string) => void
  onSetupSaved?: (setup: StudySetup) => void
  onContactStudySupport?: (setup: StudySetup) => void
  onBack: () => void
}) {
  const { lifecycle, initialState, start, onDraftSaved } = useStudyBuilderPage({
    appView,
    visit,
    shopify,
    expectedShop,
    scope: verifiedScope,
  })
  const { state } = lifecycle
  const deletion = lifecycle.data.applied['initial-state']?.deletion
  if (state.status === 'ready' && deletion)
    return <DirectStudyDeletion shopify={shopify} receipt={deletion} onBack={onBack} lifecycle={lifecycle} />
  const pageCopy = shopifyComponentCopy.studyBuilderPage
  const accepted = start?.authoring?.state.latestRunId ? start.authoring : null
  if (state.status === 'ready' && accepted && !accepted.state.canEdit)
    return (
      <s-page heading={pageHeading(accepted.authoring.draft.briefDraft.studyName)} inlineSize="large">
        <ShopifyBackActionButton action={{ content: pageCopy.testsBack, onAction: onBack }} />
        <DirectStudyDeleteControl
          shopify={shopify}
          studyId={accepted.authoring.studyId}
          state={accepted.state}
          title={pageHeading(accepted.authoring.draft.briefDraft.studyName)}
          lifecycle={lifecycle}
          onDeleted={onBack}
        />
        <Banner heading={pageCopy.startedHeading}>
          <s-paragraph>{pageCopy.startedBody}</s-paragraph>
          <s-button href={buildDirectStudyResultsViewUrl(accepted.authoring.studyId)}>
            {shopifyComponentCopy.studySetupCard.viewResults002}
          </s-button>
          <s-button href={buildStudyBuilderViewUrl({ draftId: crypto.randomUUID(), entry: 'new' })}>
            {shopifyComponentCopy.preparedSetupsPanel.createNewStudy004}
          </s-button>
        </Banner>
      </s-page>
    )
  let body = <ShopifyPageSkeleton accessibilityLabel={shopifyComponentCopy.studyBuilderPanel.loadingSetup007} />
  if (state.status === 'error') {
    // The Worker sends the load-error words for every state on the connected status, before the builder mounts; the
    // app renders them by the name it derived and never composes a sentence. If they are missing the connection is not
    // the one the builder was entered with — fail loud rather than invent copy.
    const words = readiness?.builderLoadError?.[state.error.name]
    if (!words) throw new Error('STUDY_BUILDER_LOAD_ERROR_WORDS_MISSING')
    body = (
      <StudyBuilderLoadError
        onRetry={() => {
          lifecycle.retryFailedLoad()
          void initialState.refetch()
        }}
        name={state.error.name}
        words={words}
        setupId={appView.setupId}
        onBack={onBack}
      />
    )
  } else if (state.status === 'ready' && start && verifiedScope) {
    const newerWaiting = state.newerWaiting.length > 0
    const refreshFailureNotice =
      state.refreshFailed || initialState.isError ? (
        <RequestFailureBanner
          message={pageCopy.refreshSetupFailed}
          pending={initialState.isFetching}
          onRetry={initialState.refetch}
        />
      ) : null
    body = (
      <>
        {refreshFailureNotice}
        {newerWaiting ? (
          // A newer saved version never replaces unsaved edits; the merchant chooses (#4264 `refreshed` rule).
          <Banner tone="warning">
            <s-stack gap="base">
              <s-paragraph>{pageCopy.newerVersion}</s-paragraph>
              <s-stack direction="inline" gap="base">
                <s-button onClick={lifecycle.dismissNewer}>{pageCopy.keepEditing}</s-button>
                <s-button onClick={lifecycle.applyNewer}>{pageCopy.loadSavedVersion}</s-button>
              </s-stack>
            </s-stack>
          </Banner>
        ) : null}
        <StudyBuilderEditor
          key={`${visit}:${lifecycle.data.revision}`}
          appView={appView}
          visit={visit}
          shopify={shopify}
          scope={verifiedScope}
          readiness={readiness}
          start={start}
          lifecycle={lifecycle}
          onDraftSaved={onDraftSaved}
          onSetupSaved={onSetupSaved}
          onStudyPrepared={onStudyPrepared}
          onContactStudySupport={onContactStudySupport}
          onDeleted={onBack}
          onBack={onBack}
        />
      </>
    )
  }

  // The shell exists before evaluation. Both editable and started views use the checked bootstrap title and one
  // fallback rule; the server's canEdit permission above owns whether an accepted test may open the editor.
  const heading = pageHeading(start?.snapshot.briefDraft.studyName, appView.entry)
  const backAction =
    state.status === 'ready' ? null : (
      <ShopifyBackActionButton action={{ content: pageCopy.testsBack, onAction: onBack }} />
    )
  return (
    <s-page heading={heading} inlineSize="large">
      {backAction}
      {body}
    </s-page>
  )
}
